import { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { AppHeader, AuthContext } from '../App';
import { 
  Building, 
  CheckCircle, 
  MapPin, 
  FileText, 
  Calendar,
  ArrowRight,
  X
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const ProjectsModule = () => {
  const [mijnperceelProjects, setMijnperceelProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGemeente, setSelectedGemeente] = useState('');
  const [cityOptions, setCityOptions] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  
  const { user, logout } = useContext(AuthContext);

  useEffect(() => {
    fetchMijnperceelProjects();
  }, []);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchMijnperceelProjects = async () => {
    try {
      const response = await axios.get(`${API}/mijnperceel/projects`);
      setMijnperceelProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch MijnPerceel projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    setCitiesLoading(true);
    try {
      let response;
      try {
        response = await axios.get(`${API}/location/cities/by-access`);
      } catch (primaryError) {
        console.warn('Primary city access endpoint failed, falling back to admin endpoint:', primaryError?.response?.status || primaryError);
        response = await axios.get(`${API}/location/cities`);
      }

      const normalized = Array.isArray(response.data)
        ? Array.from(
            response.data
              .filter((city) => city?.name && city?.zipcode)
              .reduce((map, city) => {
                const key = `${city.name}|${city.zipcode}`;
                if (!map.has(key)) {
                  map.set(key, { municipality: city.name, zipcode: city.zipcode });
                }
                return map;
              }, new Map())
              .values()
          ).sort((a, b) => {
            const nameCompare = a.municipality.localeCompare(b.municipality);
            if (nameCompare !== 0) return nameCompare;
            return a.zipcode.localeCompare(b.zipcode);
          })
        : [];
      setCityOptions(normalized);
    } catch (error) {
      console.error('Failed to fetch cities list:', error);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleConvertToLeads = async (selectedProjects) => {
    try {
      const conversions = [];
      for (const project of selectedProjects) {
        const response = await axios.post(`${API}/mijnperceel/convert-to-lead`, {
          mijnperceel_project_id: project.id,
          create_lead: true
        });
        
        if (response.data.success) {
          conversions.push({
            project: project,
            conversion_id: response.data.conversion_id,
            lead_id: response.data.lead_id
          });
        }
      }
      
      if (conversions.length > 0) {
        alert(`${conversions.length} projecten zijn omgezet naar leads!`);
        await fetchMijnperceelProjects();
      }
    } catch (error) {
      console.error('Conversion failed:', error);
      alert('Conversie naar leads mislukt.');
    }
  };

  const handleRemoveConversion = async (projectId) => {
    try {
      const conversionsResponse = await axios.get(`${API}/mijnperceel/projects/${projectId}/conversions`);
      
      if (conversionsResponse.data.success && conversionsResponse.data.is_converted_by_user) {
        const userConversion = conversionsResponse.data.conversions.find(conv => 
          conv.user_id === user?.id
        );
        
        if (userConversion) {
          const response = await axios.delete(`${API}/mijnperceel/convert-to-lead/${userConversion.id}`);
          
          if (response.data.success) {
            alert('Project conversie ongedaan gemaakt!');
            await fetchMijnperceelProjects();
          }
        }
      }
    } catch (error) {
      console.error('Remove conversion failed:', error);
      alert('Conversie ongedaan maken mislukt.');
    }
  };

  // Filter projects
  const filteredProjects = mijnperceelProjects.filter(project => {
    let matches = true;
    
    if (searchTerm) {
      matches = matches && (
        (project.project_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.project_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.formatted_address || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedGemeente && selectedGemeente !== 'all') {
      // selectedGemeente is in format "Municipality|Zipcode"
      const [municipality, zipcode] = selectedGemeente.split('|');
      matches = matches && project.municipality === municipality && project.zipcode === zipcode;
    }
    
    return matches;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader 
        user={user} 
        currentPage="projecten"
        onNavigate={(path) => window.location.href = path}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Projecten</h2>
          <p className="text-gray-600">Bekijk en converteer MijnPerceel projecten naar leads</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Beschikbare projecten</p>
                  <p className="text-2xl font-bold text-gray-900">{mijnperceelProjects.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Omgezet naar leads</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {mijnperceelProjects.filter(p => p.is_converted_to_lead).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Gemeenten</p>
                  <p className="text-2xl font-bold text-gray-900">{cityOptions.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start mb-6">
              <div className="flex flex-wrap gap-4 items-center">
                <Button
                  variant="outline"
                  onClick={() => handleConvertToLeads(filteredProjects.filter(p => !p.is_converted_to_lead))}
                  disabled={filteredProjects.filter(p => !p.is_converted_to_lead).length === 0}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Converteer naar Leads ({filteredProjects.filter(p => !p.is_converted_to_lead).length})
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-64">
                <Input
                  type="text"
                  placeholder="Zoek projecten..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div className="min-w-64">
                <select
                  value={selectedGemeente}
                  onChange={(e) => setSelectedGemeente(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">
                    {citiesLoading && cityOptions.length === 0 ? 'Gemeenten laden...' : 'Alle gemeenten'}
                  </option>
                  {cityOptions.length === 0 && !citiesLoading && (
                    <option value="" disabled>
                      Geen gemeenten beschikbaar
                    </option>
                  )}
                  {cityOptions.map(({ municipality, zipcode }) => {
                    const key = `${municipality}|${zipcode}`;
                    return (
                      <option key={key} value={key}>
                        {municipality} ({zipcode})
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              MijnPerceel Projecten ({filteredProjects.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Projecten laden...</div>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500">
                  {mijnperceelProjects.length === 0 
                    ? 'Geen projecten beschikbaar. Vraag de super admin om data te synchroniseren.' 
                    : 'Geen projecten voldoen aan de filters.'
                  }
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-gray-900">
                          {project.project_name || project.project_number}
                        </h3>
                        <Badge variant={project.is_converted_to_lead ? 'default' : 'secondary'}>
                          {project.is_converted_to_lead ? 'Lead' : 'Project'}
                        </Badge>
                        {project.status && (
                          <Badge variant="outline" className="text-xs">
                            {project.status}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>{project.project_number}</span>
                        </div>
                        {project.municipality && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{project.municipality}</span>
                          </div>
                        )}
                        {project.treating_authority && (
                          <div className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            <span>{project.treating_authority}</span>
                          </div>
                        )}
                        {project.synced_at && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(project.synced_at).toLocaleDateString('nl-NL')}</span>
                          </div>
                        )}
                      </div>
                      
                      {project.formatted_address && (
                        <p className="text-sm text-gray-700">
                          {project.formatted_address}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!project.is_converted_to_lead && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleConvertToLeads([project])}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Naar Lead
                        </Button>
                      )}
                      {project.is_converted_to_lead && (
                        <>
                          <Badge variant="default" className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Omgezet
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveConversion(project.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Ongedaan
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProjectsModule;
